import {useModal} from '@appscience/hooks'
import {useHotkeys} from '@mantine/hooks'
import {TableNode} from '@table-library/react-table-library'
import {downloadAsCsv, verify, TitleWithId} from '@appscience/utils'
import {useDataTableContext} from '../../../context'
import {DataTableColumnsExport, ExportCsvData, ExportCsvType} from '../model'
import {PreExportPopup} from './popups/pre-export-popup'

const DEFAULT_COLUMNS_EXPORT: ExportCsvType = 'onlyFull'

export function useExportPopup<ColumnID extends string, ITEM extends TableNode>(params: ExportCsvData<ColumnID, ITEM>|undefined) {
  const {allColumns, select, data} = useDataTableContext()
  const [openPopup, hidePopup] = useModal(PreExportPopup)

  function openExportPopup() {
    const {
      headerAccessor,
      contentAccessor,
    } = verify(params)
    const columnsExportData: DataTableColumnsExport = params?.columnsExportData || { type: DEFAULT_COLUMNS_EXPORT }

    const columns: Array<TitleWithId<ColumnID>> = allColumns.map(x => ({
      id: x.id as ColumnID,
      title: headerAccessor(x.id as ColumnID),
    }))

    function onExport(filename: string, columnIds: Array<string>) {
      const selectedRowIdsSet = new Set(select?.state.ids || [])
      const exportedData = selectedRowIdsSet.size === 0
        ? data
        : data.filter(row => selectedRowIdsSet.has(row.id))
      const idsSet = new Set(columnIds)

      downloadAsCsv({
        filename,
        contentAccessor: (item, columnId) => contentAccessor(item as ITEM, columnId as ColumnID),
        columns: columns.filter(x => idsSet.has(x.id)),
        data: exportedData,
      })
    }

    openPopup({
      columns,
      canSelectColumns: columnsExportData.type === 'selective',
      defaultSelectedIds: columnsExportData.type === 'selective'
        ? columnsExportData.getDefaultSelectedIds && columnsExportData.getDefaultSelectedIds()
        : undefined,
      onChange: columnsExportData?.type === 'selective' ? columnsExportData.onChange : undefined,
      onExport: onExport,
      closeFn: () => hidePopup(),
    })
  }
  useHotkeys(params ? [['ctrl+e', openExportPopup]] : [])

  return {
    openExportPopup,
  }
}